import {
    ConnectionSequenceStepConnectionType,
    ConnectionSequenceStepMeaningType,
    ConnectionSequenceStepType,
    PropertyModifyChangeType,
    PropertyModifyConditionType,
    PropertyModifyValueCompereType,
    RequestConnectionPropertyType,
    RequestEmptyFieldsBehaviorType,
    RequestMethod,
    RequestPayloadType,
    ResponsePropertySourceType,
} from "types/enums";
import { z } from "zod";

// RequestConnectionPropertyModifyData

export const requestConnectionPropertyModifySchema = z.object({
    id: z.string().optional(),
    request_connection_property_id: z.string().nullable().optional(),
    response_connection_property_id: z.string().nullable().optional(),
    request_header_config_id: z.string().nullable().optional(),
    value_compere_type: z.nativeEnum(PropertyModifyValueCompereType),
    value_compere_data_accessor: z.string().nullable(),
    condition_type: z.nativeEnum(PropertyModifyConditionType),
    condition_value: z.string().nullable(),
    value_to_change: z.string().nullable(),
    change_type: z.nativeEnum(PropertyModifyChangeType),
    condition_value2: z.string().nullable(),
    function_parameter: z.string().nullable(),
    lead_value_accessor: z.string().nullable(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
});

// RequestHeaderConfigData
export const requestHeaderConfigSchema = z.object({
    id: z.string(),
    request_config_id: z.string().nullable(),
    key: z.string(),
    value: z.string().nullable(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
    requestConnectionPropertyModify: z.array(requestConnectionPropertyModifySchema).optional(),
});

// ResponseConnectionPropertyCheckData
export const responseConnectionPropertyCheckSchema = z.object({
    id: z.string(),
    response_handler_id: z.string().nullable(),
    response_handler_match_id: z.string().nullable(),
    accessor: z.string(),
    type: z.string(),
    source: z.string(),
    operator: z.string(),
    value: z.string(),
    value2: z.string(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
});

// ConnectionSequenceStepConnectionData
export const connectionSequenceStepConnectionSchema = z.object({
    id: z.string(),
    connection_sequence_step_source_id: z.string().nullable(),
    connection_sequence_step_target_id: z.string().nullable(),
    response_handler_source_id: z.string().nullable(),
    source_form_filled: z.boolean().nullable(),
    type: z.nativeEnum(ConnectionSequenceStepConnectionType),
    wait_ms: z.number().nullable(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
});

// We'll define responseHandlerSchema below, but it references responseConnectionPropertySchema.
// We'll use z.lazy to handle circular references.

// ResponseConnectionPropertyData
export const responseConnectionPropertySchema: z.ZodType = z.lazy(() =>
    z.object({
        id: z.string(),
        response_handler_id: z.string().nullable(),
        accessor: z.string().nullable(),
        system_name: z.string(),
        description: z.string().nullable(),
        source: z.nativeEnum(ResponsePropertySourceType),
        value: z.string().nullable(),
        created_at: z.string().nullable(),
        updated_at: z.string().nullable(),
        responseHandler: responseHandlerSchema.optional(), // forward reference
        requestConnectionPropertyModify: z.array(requestConnectionPropertyModifySchema).optional(),
    })
);

// ResponseHandlerData
export const responseHandlerSchema: z.ZodType = z.lazy(() =>
    z.object({
        id: z.string(),
        request_config_id: z.string().nullable(),
        name: z.string(),
        result_meaning: z.string(),
        success_if_body_contains: z.string().nullable(),
        result_action: z.string().nullable(),
        action_value: z.string().nullable(),
        order: z.number(),
        created_at: z.string().nullable(),
        updated_at: z.string().nullable(),
        responseConnectionProperties: z.array(responseConnectionPropertySchema).optional(),
        responseConnectionPropertiesCheck: z.array(responseConnectionPropertyCheckSchema).optional(),
        stepConnection: z.array(connectionSequenceStepConnectionSchema).optional(),
    })
);

// RequestConnectionPropertyData
// requestConfig is defined below, so we use z.lazy
export const requestConnectionPropertySchema: z.ZodType = z.lazy(() =>
    z.object({
        id: z.string(),
        request_config_id: z.string().nullable(),
        response_connection_property_id: z.string().nullable(),
        request_config_url_id: z.string().nullable(),
        type: z.nativeEnum(RequestConnectionPropertyType),
        use_as_accessor: z.string(),
        accessor: z.string().nullable(),
        value: z.string().nullable(),
        created_at: z.string().nullable(),
        updated_at: z.string().nullable(),
        requestConnectionPropertyCombine: z.array(z.unknown()).optional(),
        requestConnectionPropertyModify: z.array(requestConnectionPropertyModifySchema).optional(),
        requestConfig: z.lazy(() => requestConfigSchema).optional(),
    })
);

// RequestConfigData
const requestConfigSchema: z.ZodType = z.lazy(() =>
    z.object({
        id: z.string(),
        name: z.string(),
        method: z.nativeEnum(RequestMethod),
        url: z.string().min(1, "forms.general-validation.url_required"),
        timeout_ms: z.number().nonnegative(),
        send_payload_as: z.nativeEnum(RequestPayloadType),
        empty_fields_behavior: z.nativeEnum(RequestEmptyFieldsBehaviorType).nullable(),
        created_at: z.string().nullable(),
        updated_at: z.string().nullable(),
        offerConnectionSequenceSteps: z.array(z.unknown()).optional(),
        requestConnectionProperties: z.array(requestConnectionPropertySchema).optional(),
        responseHandlers: z.array(responseHandlerSchema).optional(),
        requestConnectionPropertiesUrl: z.array(requestConnectionPropertySchema).optional(),
        requestConnectionPropertiesPathParam: z.array(responseConnectionPropertySchema).optional(),
        headers: z.array(requestHeaderConfigSchema).optional(),
    })
);

// ConnectionSequenceStepMeaningData
const meaningSchema = z.object({
    id: z.string(),
    connection_sequence_step_id: z.string(),
    type: z.nativeEnum(ConnectionSequenceStepMeaningType),
});

// ConnectionSequenceStepData
export const methodSchema = z.object({
    id: z.string().nullable().optional(),
    tempId: z.union([z.string(), z.number().int().positive()]),
    name: z.string(),
    position_x: z.number(),
    position_y: z.number(),
    requestConfig: requestConfigSchema.optional(),
    type: z.nativeEnum(ConnectionSequenceStepType),
    connection_sequence_step_verify_phone_id: z.string().nullable(),
    connection_sequence_step_next_id: z.string().nullable(),
    wait_ms: z.number().nullable(),
    model_type: z.string(),
    model_id: z.string(),
    meanings: z.array(meaningSchema).optional(),
    created_at: z.unknown().nullable(),
    updated_at: z.unknown().nullable(),
});

export const schema = z.object({
    methods: z.array(methodSchema).optional(),
});

// Export the inferred type from the schema
export type FormData = z.infer<typeof schema>;
export type ConnectionSequenceStepFormData = z.infer<typeof methodSchema>;
